import React from 'react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'src/design/atoms/Container';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Select } from 'src/design/atoms/Select';
import {
  useWorkflowTemplate,
  useWorkflowTemplateByType,
} from 'src/api/queries/workflows/useWorkflowTemplate';
import { useDeleteWorkflowTemplate } from 'src/api/queries/workflows/useWorkflowTemplateMutations';
import { CreateWorkflow } from 'src/app/workflows/CreateWorkflow/CreateWorkflow';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Header } from 'src/design/organisms/Header';

const WORKFLOW_OPTIONS = [
  { id: 'rfi', label: 'RFI Workflow' },
  { id: 'change', label: 'Change Order Workflow' },
  { id: 'submittal', label: 'Submittal Workflow' },
  { id: 'drawing', label: 'Drawing Workflow' },
  { id: 'transmittal', label: 'Transmittal Workflow' },
  { id: 'timesheet', label: 'Timesheet Workflow' },
];

export const EditWorkflowTemplate: React.FC = () => {
  const params = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const isCreate = params.templateId === 'create';

  const [workflowType, setWorkflowType] = React.useState<string | null>(null);
  const deleteTemplate = useDeleteWorkflowTemplate();

  const byId = useWorkflowTemplate(
    isCreate || !params.templateId ? null : Number(params.templateId),
  );
  const byType = useWorkflowTemplateByType(isCreate ? workflowType : null);

  const { data: workflowSettings, isLoading } = isCreate ? byType : byId;
  const workflowSteps = workflowSettings?.steps ?? [];

  const workflowData = React.useMemo(
    () =>
      WORKFLOW_OPTIONS.map((option) => ({
        value: option.id,
        label: option.label,
      })),
    [],
  );

  const removeWorkflow = async () => {
    await deleteTemplate.mutateAsync(Number(params.templateId));
    navigate('/settings/workflows');
  };

  if (isLoading) return <Loader />;

  const actionComponent = isCreate ? (
    <BasicButton onClick={() => navigate(-1)}>Cancel</BasicButton>
  ) : null;

  return (
    <>
      <Header
        title="Workflows"
        breadcrumbProps={{
          items: [
            { text: 'Settings' },
            { text: 'Workflows', href: '/settings/workflows' },
            { text: workflowSettings?.name || 'New' },
          ],
        }}
        helpText="Configure the approval steps and notification behavior for this workflow template."
        actions={actionComponent}
      />
      {isCreate && (
        <Container size="md">
          <Stack gap="lg">
            <Paper p="md" withBorder>
              <SecondaryButton
                onClick={() => navigate('/settings/workflows')}
                style={{ marginBottom: 10 }}
              >
                Back
              </SecondaryButton>

              <Select
                placeholder="Select workflow type..."
                data={workflowData}
                value={workflowType}
                onChange={setWorkflowType}
                clearable={false}
                searchable
              />
            </Paper>
          </Stack>
        </Container>
      )}

      {workflowSettings && (
        <CreateWorkflow
          initialSettings={workflowSettings}
          initialSteps={workflowSteps}
          isTemplateEditor
          onNavigate={() => navigate('/settings/workflows')}
          deleteWorkflow={
            workflowSettings.isSystem ? undefined : removeWorkflow
          }
          readOnly={workflowSettings.isSystem}
        />
      )}
    </>
  );
};
